import type {RGBA} from '@Utils/color.util';

export enum FillTypes {
  SOLID = 0,
  LINEAR_GRADIENT = 1,
  RADIAL_GRADIENT = 2,
  NONE = 3,
}

export interface GradientStop {
  color: RGBA;
  /** Position along the gradient, 0 at its start and 1 at its end. */
  offset: number;
}

/** A point inside the box, as fractions of its width (x) and height (y). */
export interface FillPoint {
  x: number;
  y: number;
}

/** A circle keeps its shape whatever the box; an ellipse stretches with it, each axis a fraction of its own side. */
export enum RadialGradientShape {
  CIRCLE = 'circle',
  ELLIPSE = 'ellipse',
}

/**
 * A fill that paints nothing. `color` is the one it painted before it was switched off in this editing session, so switching it back on
 * restores it. It is never saved: a loaded fill has none, and switches on to the default of what it fills.
 */
export interface NoFillObject {
  fillType: FillTypes.NONE;
  color: RGBA | null;
}

/** A fill that paints nothing, as it is saved: without the color kept while editing. */
export interface SavedNoFillObject {
  fillType: FillTypes.NONE;
}

export interface SolidFillObject {
  fillType: FillTypes.SOLID;
  color: RGBA;
}

export interface LinearGradientFillObject {
  fillType: FillTypes.LINEAR_GRADIENT;
  /** Ascending by offset. */
  stops: GradientStop[];
  /** CSS `linear-gradient` convention: 0 points up, 90 right, 180 down. The line spans the box corner to corner along that direction. */
  angle: number;
}

export interface RadialGradientFillObject {
  fillType: FillTypes.RADIAL_GRADIENT;
  /** Ascending by offset. */
  stops: GradientStop[];
  center: FillPoint;
  /** Radius of the outermost stop: a fraction of the box's longer side for a circle, of each side for an ellipse. */
  radius: number;
  shape: RadialGradientShape;
}

export type GradientFillObject = LinearGradientFillObject | RadialGradientFillObject;

/** What a surface is painted with. Each type stores only what it draws with, so a fill is always written whole. */
export type FillObject = NoFillObject | SolidFillObject | GradientFillObject;

/** A fill as it is saved. */
export type SavedFillObject = SavedNoFillObject | SolidFillObject | GradientFillObject;

/** The size of the box a gradient is shown in; only the ratio of the sides matters. */
export interface FillBoxSize {
  width: number;
  height: number;
}

export const SQUARE_FILL_BOX: FillBoxSize = {width: 1, height: 1};

export const DEFAULT_FILL_COLOR: RGBA = [0, 0, 0, 1];

export const isGradientFillObject = (fill: FillObject): fill is GradientFillObject => {
  return fill.fillType === FillTypes.LINEAR_GRADIENT || fill.fillType === FillTypes.RADIAL_GRADIENT;
};

export const hasFill = (fill: FillObject): boolean => {
  return fill.fillType !== FillTypes.NONE;
};

export const hasLinearGradientFill = (fill: FillObject): boolean => {
  return fill.fillType === FillTypes.LINEAR_GRADIENT;
};

export const hasRadialGradientFill = (fill: FillObject): boolean => {
  return fill.fillType === FillTypes.RADIAL_GRADIENT;
};

export const hasGradientFill = (fill: FillObject): boolean => {
  return isGradientFillObject(fill);
};

export const createSolidFill = (color: RGBA): SolidFillObject => {
  return {fillType: FillTypes.SOLID, color};
};

export const createTransparentFill = (color: RGBA | null = null): NoFillObject => {
  return {fillType: FillTypes.NONE, color};
};

/** The fill as it is saved: one that paints nothing drops the color it kept while editing. */
export const toSavedFillObject = (fill: FillObject): SavedFillObject => {
  return fill.fillType === FillTypes.NONE ? {fillType: FillTypes.NONE} : fill;
};

/** A saved fill as the editor holds it: one that paints nothing has no color to switch back on to. */
export const fromSavedFillObject = (fill: SavedFillObject): FillObject => {
  return fill.fillType === FillTypes.NONE ? createTransparentFill() : fill;
};

/** The colors a fill paints with: none for a fill that paints nothing. */
export const getFillColors = (fill: FillObject): RGBA[] => {
  return hasFill(fill) ? getStoredFillColors(fill) : [];
};

/** The colors a fill stores, the one a fill that paints nothing kept while editing included: what a color picker edits. */
export const getStoredFillColors = (fill: FillObject): RGBA[] => {
  if (isGradientFillObject(fill)) {
    return fill.stops.map((stop) => stop.color);
  }
  return fill.color !== null ? [fill.color] : [];
};

/** The first color the fill stores, or null for a fill that paints nothing and kept no color. */
export const getFirstStoredFillColor = (fill: FillObject): RGBA | null => {
  const [firstColor] = getStoredFillColors(fill);
  return firstColor ?? null;
};

/** The opacity of the fill's first color, which every color of a fill shares; a fill that paints nothing is fully transparent. */
export const getFillAlpha = (fill: FillObject): number => {
  return hasFill(fill) ? getStoredFillColors(fill)[0][3] : 0;
};

/** The fill with every color at `alpha`. A fill that paints nothing has no opacity to change, so the color it returns to stays as it was. */
export const withFillAlpha = (fill: FillObject, alpha: number): FillObject => {
  if (!hasFill(fill)) {
    return fill;
  }
  return withFillColors(
    fill,
    getStoredFillColors(fill).map((color): RGBA => [color[0], color[1], color[2], alpha])
  );
};

/**
 * The fill painting `colors` instead, keeping its type and layout. A gradient given as many colors as it has stops keeps their offsets;
 * given another number, it spreads them evenly between its first and last stop.
 */
export const withFillColors = <T extends FillObject>(fill: T, colors: RGBA[]): T => {
  if (!isGradientFillObject(fill)) {
    return {...fill, color: colors[0]};
  }
  const firstOffset = fill.stops[0]?.offset ?? 0;
  const lastOffset = fill.stops[fill.stops.length - 1]?.offset ?? 1;
  const stops =
    colors.length === fill.stops.length ? fill.stops.map((stop, index) => ({color: colors[index], offset: stop.offset})) : getEvenStops(colors, firstOffset, lastOffset);
  return {...fill, stops};
};

/** Stops for `colors` spread evenly from `startOffset` to `endOffset`. */
export const getEvenStops = (colors: RGBA[], startOffset: number, endOffset: number): GradientStop[] => {
  const lastIndex = Math.max(1, colors.length - 1);
  return colors.map((color, index) => {
    return {color, offset: startOffset + ((endOffset - startOffset) * index) / lastIndex};
  });
};

/** Fresh arrays and objects all the way down: a stored fill is frozen once it reaches the store, and a later edit must never write into it. */
export const cloneFillObject = <T extends FillObject>(fill: T): T => {
  return structuredClone(fill);
};

/** Whether a fill update is a whole fill of its type: a partial one cannot be merged into a fill of another type. */
export const isCompleteFillObject = (value: unknown): value is FillObject => {
  if (typeof value !== 'object' || value === null || !('fillType' in value)) {
    return false;
  }
  switch (value.fillType) {
    case FillTypes.NONE:
    case FillTypes.SOLID:
      return 'color' in value;
    case FillTypes.LINEAR_GRADIENT:
      return 'stops' in value && 'angle' in value;
    case FillTypes.RADIAL_GRADIENT:
      return 'stops' in value && 'center' in value && 'radius' in value && 'shape' in value;
    default:
      return false;
  }
};
